// apps/web/src/components/ideas/IdeaColumn.tsx
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import type { Idea, IdeaStatus } from "@/hooks/use-ideas";
import { IdeaCard } from "./IdeaCard";

interface Props {
  status: IdeaStatus;
  title: string;
  ideas: Idea[];
  disableDrop?: boolean;
  onCardClick: (idea: Idea) => void;
  onEdit: (idea: Idea) => void;
  onDelete: (idea: Idea) => void;
  onPromote: (idea: Idea) => void;
  canEdit: boolean;
}

export function IdeaColumn({ status, title, ideas, disableDrop, onCardClick, onEdit, onDelete, onPromote, canEdit }: Props) {
  const { t } = useTranslation("ideas");
  return (
    <div className="flex flex-col rounded-lg border bg-muted/30 p-3 min-h-[60vh]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
        <span className="text-xs text-muted-foreground">{ideas.length}</span>
      </div>
      <Droppable droppableId={status} isDropDisabled={!!disableDrop || !canEdit}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="flex flex-col gap-2 flex-1"
          >
            {ideas.length === 0 && (
              <div className="rounded border border-dashed p-4 text-center text-xs text-muted-foreground">
                {disableDrop ? t("ideas:column.noConverted") : t("ideas:column.dropHere")}
              </div>
            )}
            {ideas.map((idea, idx) => (
              <Draggable key={idea.id} draggableId={idea.id} index={idx} isDragDisabled={!canEdit || !!disableDrop}>
                {(prov) => (
                  <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}>
                    <IdeaCard
                      idea={idea}
                      onClick={() => onCardClick(idea)}
                      onEdit={() => onEdit(idea)}
                      onDelete={() => onDelete(idea)}
                      onPromote={status === "SELECTED" ? () => onPromote(idea) : undefined}
                      canEdit={canEdit}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}
